import { formatISO } from "date-fns";
import { supabase } from "@/lib/supabase";
import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import camelcaseKeys from "camelcase-keys";

import {
  addCompetitor,
  addCompetitorFailure,
  addCompetitorSuccess,
  changeCompetitorStatus,
  changeCompetitorStatusFailure,
  changeCompetitorStatusSuccess,
  createNewBrand,
  deleteCompetitor,
  deleteCompetitorFailure,
  deleteCompetitorSuccess,
  fetchAddNewBrandCompetitors,
  fetchAddNewBrandCompetitorsFailure,
  fetchAddNewBrandCompetitorsSuccess,
  fetchBrandFailure,
  fetchBrands,
  fetchBrandsSuccess,
  fetchBrandSuccess,
  fetchListBrandsSuccess,
  resetAddNewBrand,
  setAddNewBrandLoading,
  setBrandsLoading,
  setSelectedBrandId,
  fetchSelectedBrandIdSuccess,
  updateBrand,
  updateBrandFailure,
  updateBrandSuccess,
  fetchSelectedBrandId,
  updateSelectedBrandId,
} from "./slice";
import { setSubscriptionPlan } from "../auth/slice";
import {
  AddCompetitorPayload,
  ChangeCompetitorStatusPayload,
  DeleteCompetitorPayload,
  FetchAddNewBrandCompetitorsPayload,
  UpdateBrandPayload,
} from "./types";
import {
  applySagaError,
  fetchCompetitorsSaga,
  reserchScoresSaga,
  isFiniteBrandId,
  parseMetaSelectedBrandId,
} from "../utils/saga";
import { throwOnSupabaseError } from "../../utils/errors";
import {
  selectAuthAccountId,
  selectAuthCompany,
  selectAuthIsMember,
  selectAuthUserID,
} from "../selectors/auth";
import { BRANDS_TABLE_NAME, USERS_META_TABLE_NAME } from "../const";
import { Brand } from "../../types/brand";
import { selectAddNewBrandCompetitorsState, selectAddNewBrandSelectedCompetitorsState, selectBrandState, selectSelectedBrandIdState } from "../selectors/brands";
import { addPrompt } from "../prompts/slice";
import analytics from "@/lib/Analytics";
import { Company, CompetitorsResponse } from "../auth/types";
import { CompetitorWithLogo } from "../../types/competitor";
import {
  fetchCompetitorLogosBatch,
  fetchLogoDescribeByDomain,
  mapCompetitorsWithLogoAndDescription,
} from "../utils/fetchCompetitorLogos";
import { updateSelectedBrandIdSaga } from "./sagas/updateSelectedBrandIdSaga";

function* fetchBrandsSaga() {
  const userID: string | null = yield select(selectAuthAccountId);

  if (!userID) {
    return;
  }

  const selectedBrandId: number | undefined = yield select(selectSelectedBrandIdState);

  try {
    yield put(setBrandsLoading());

    const { data: brandsData, error: brandsError } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .select("*")
        .is("deleted_at", null)
        .eq("user_id", userID)
        .eq("competitor", false)
        .eq("is_active", true)
    );

    throwOnSupabaseError(brandsError);

    const normalizedBrands = camelcaseKeys(brandsData);
    yield put(fetchListBrandsSuccess(normalizedBrands));

    // No brands or no selected brand — list is loaded, finish early
    if (!normalizedBrands.length || !isFiniteBrandId(selectedBrandId)) {
      yield put(fetchBrandsSuccess());
      return;
    }

    const brandId = selectedBrandId;

    const { data: competitorsData, error: competitorsError } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .select("*")
        .is("deleted_at", null)
        .eq("user_id", userID)
        .eq("competitor", true)
        .eq("main_brand_id", brandId)
    );

    throwOnSupabaseError(competitorsError);

    const normalizedCompetitors = camelcaseKeys(competitorsData);

    // If selectedBrandId is stale (not in user's brand list), fall back to first available brand
    const matchingBrand = normalizedBrands.find(({ id }: Brand) => id === brandId);
    const effectiveBrand = matchingBrand ?? normalizedBrands[0];

    if (!matchingBrand && effectiveBrand) {
      yield put(setSelectedBrandId(effectiveBrand.id));
    }

    if (effectiveBrand) {
      // Re-fetch competitors for the effective brand if it changed
      const effectiveBrandId = effectiveBrand.id;
      let effectiveCompetitors = normalizedCompetitors;
      if (!matchingBrand && effectiveBrand) {
        const { data: fallbackCompetitors, error: fallbackErr } = yield call(() =>
          supabase
            .from(BRANDS_TABLE_NAME)
            .select("*")
            .is("deleted_at", null)
            .eq("user_id", userID)
            .eq("competitor", true)
            .eq("main_brand_id", effectiveBrandId)
        );
        throwOnSupabaseError(fallbackErr);
        effectiveCompetitors = camelcaseKeys(fallbackCompetitors);
      }
      yield put(fetchBrandSuccess({ brand: effectiveBrand, competitors: effectiveCompetitors }));
    }

    yield put(fetchBrandsSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "brands.fetchBrands",
      failureAction: fetchBrandFailure,
    });
  }
}

function* addCompetitorSaga(action: PayloadAction<AddCompetitorPayload>) {
  try {
    yield put(setBrandsLoading());
    const userId: string | null = yield select(selectAuthAccountId);
    if (!userId) return;

    const selectedBrandId: number | undefined = yield select(selectSelectedBrandIdState);

    const { name, logoUrl, website, description: descriptionFromPayload } = action.payload;

    let description = descriptionFromPayload ?? "";
    if (!description && website) {
      const described: Awaited<ReturnType<typeof fetchLogoDescribeByDomain>> = yield call(
        fetchLogoDescribeByDomain,
        website,
      );
      description = described.description ?? "";
    }

    const { error, data } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .insert({
          name,
          logo_url: logoUrl,
          website,
          description,
          competitor: true,
          main_brand_id: selectedBrandId,
          user_id: userId,
        })
        .select()
    );

    throwOnSupabaseError(error);

    yield put(fetchBrands());
    yield put(addCompetitorSuccess());

    analytics.track('Competitor Added', {
      competitor_name: name,
      competitor_domain: website
    });

    yield put(addPrompt({
      content: `Top alternatives to ${name}`,
    }));
    yield call(reserchScoresSaga, userId, false, undefined, data[0].id);
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "brands.addCompetitor",
      failureAction: addCompetitorFailure,
    });
  }
}

function* changeCompetitorStatusSaga(
  action: PayloadAction<ChangeCompetitorStatusPayload>,
) {
  try {
    yield put(setBrandsLoading());
    const { id, isActive } = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);

    const { error } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .update({ is_active: isActive })
        .eq("id", id)
        .eq("user_id", userID)
    );

    throwOnSupabaseError(error);

    yield put(fetchBrands());
    yield put(changeCompetitorStatusSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "brands.changeCompetitorStatus",
      failureAction: changeCompetitorStatusFailure,
    });
  }
}

function* deleteCompetitorSaga(action: PayloadAction<DeleteCompetitorPayload>) {
  try {
    yield put(setBrandsLoading());
    const { id } = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);

    const { data, error } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .update({ deleted_at: formatISO(new Date()) })
        .eq("id", id)
        .eq("user_id", userID)
        .select()
    );

    throwOnSupabaseError(error);

    analytics.track('Competitor Deleted', {
      competitor_name: data[0].name,
      competitor_domain: data[0].website
    });

    yield put(fetchBrands());
    yield put(deleteCompetitorSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "brands.deleteCompetitor",
      failureAction: deleteCompetitorFailure,
    });
  }
}

function* updateBrandSaga(action: PayloadAction<UpdateBrandPayload>) {
  try {
    yield put(setBrandsLoading());
    const { name, description, website, region, brandType, industry, primaryLanguage, targetAudience } = action.payload;
    const userID: string | null = yield select(selectAuthAccountId);
    const brand: Brand | null = yield select(selectBrandState);

    const updateData: Record<string, unknown> = { name };
    if (description !== undefined) updateData.description = description;
    if (website !== undefined) updateData.website = website;
    if (region !== undefined) updateData.region = region;
    if (brandType !== undefined) updateData.brand_type = brandType;
    if (industry !== undefined) updateData.industry = industry;
    if (primaryLanguage !== undefined) updateData.primary_language = primaryLanguage;
    if (targetAudience !== undefined) updateData.target_audience = targetAudience;

    const { error } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .update(updateData)
        .eq("id", brand?.id)
        .eq("user_id", userID)
    );

    throwOnSupabaseError(error);

    yield put(fetchBrands());
    yield put(updateBrandSuccess());

    analytics.track('Brand Settings Updated', {
      previous_name: brand?.name,
      new_name: name
    });
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "brands.updateBrand",
      failureAction: updateBrandFailure,
    });
  }
}

function* fetchAddNewBrandCompetitorsSaga(action: PayloadAction<FetchAddNewBrandCompetitorsPayload>) {
  try {
    yield put(setAddNewBrandLoading());
    const data: CompetitorsResponse = yield call(fetchCompetitorsSaga, action.payload);

    if (!data.competitors?.length) {
      yield put(fetchAddNewBrandCompetitorsSuccess([]));
      return;
    }

    const logosMap: Awaited<ReturnType<typeof fetchCompetitorLogosBatch>> = yield call(
      fetchCompetitorLogosBatch,
      data.competitors
    );

    const competitorsWithLogo = mapCompetitorsWithLogoAndDescription(
      data.competitors,
      logosMap,
    );

    yield put(fetchAddNewBrandCompetitorsSuccess(competitorsWithLogo));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "brands.fetchAddNewBrandCompetitors",
      failureAction: fetchAddNewBrandCompetitorsFailure,
    });
  }
}

function* createNewBrandSaga() {
  try {
    const accountId: string | null = yield select(selectAuthAccountId);
    if (!accountId) return;

    const company: Company = yield select(selectAuthCompany);
    const competitors: CompetitorWithLogo[] | null = yield select(selectAddNewBrandCompetitorsState);
    const selectedCompetitors: CompetitorWithLogo[] | null = yield select(selectAddNewBrandSelectedCompetitorsState);

    const { data, error } = yield call(() =>
      supabase
        .from(BRANDS_TABLE_NAME)
        .insert({
          name: company.name,
          website: company.website,
          logo_url: company.logoUrl,
          description: company.description ?? "",
          region: company.region ?? null,
          brand_type: company.brandType ?? null,
          industry: company.industry ?? null,
          primary_language: company.primaryLanguage ?? 'English',
          target_audience: company.targetAudience ?? null,
          competitor: false,
          is_active: true,
          user_id: accountId,
        })
        .select()
    );

    throwOnSupabaseError(error);

    const brand: Brand = camelcaseKeys(data)[0];

    if (competitors && competitors.length) {
      const selectedCompetitorsSet = new Set((selectedCompetitors || []).map(c => c.name));
      const competitorsData = competitors.map(competitor => ({
        name: competitor.name,
        website: competitor.website,
        logo_url: competitor.logoUrl,
        description: competitor.description ?? "",
        competitor: true,
        is_active: selectedCompetitorsSet.has(competitor.name),
        user_id: accountId,
        main_brand_id: brand.id,
      }));

      const { error: competitorsError } = yield call(() =>
        supabase
          .from(BRANDS_TABLE_NAME)
          .insert(competitorsData)
      );

      throwOnSupabaseError(competitorsError);
    }

    yield call(reserchScoresSaga, accountId, true, undefined, undefined, brand.id);
    yield put(fetchBrands());
    yield put(setSelectedBrandId(brand.id));

    try {
      yield call(() =>
        supabase.functions.invoke("update-stripe-quantity", {
          body: JSON.stringify({ user_id: accountId }),
        })
      );
    } catch (stripeError) {
      console.error("Failed to update Stripe quantity:", stripeError);
    }

  } catch (error) {
    yield* applySagaError(error, { operationName: "brands.createNewBrand" });
  } finally {
    yield put(resetAddNewBrand());
  }
}

function* fetchUsersMeta(userId: string) {
  const { data, error } = yield call(() =>
    supabase.from(USERS_META_TABLE_NAME).select("*").eq("user_id", userId).single()
  );
  throwOnSupabaseError(error);
  return data;
}

export function* fetchSelectedBrandIdSaga(action: PayloadAction<string | undefined>) {
  try {
    const isMember: boolean = yield select(selectAuthIsMember);
    const ownerId: string | null = yield select(selectAuthAccountId);
    const userId: string | undefined = yield select(selectAuthUserID);
    const overrideUserId = action.payload;

    if (isMember && userId && !overrideUserId) {
      const memberMeta = yield* fetchUsersMeta(userId);
      const memberBrandId = memberMeta?.selected_brand_id;

      // Always fetch owner's meta for subscription plan — members inherit the owner's tier
      let ownerMeta: Record<string, unknown> | null = null;
      if (ownerId) {
        ownerMeta = yield* fetchUsersMeta(ownerId);
      }

      const memberResolved = parseMetaSelectedBrandId(memberBrandId);
      if (memberResolved !== undefined) {
        yield put(setSelectedBrandId(memberResolved));
      } else {
        const ownerResolved = parseMetaSelectedBrandId(ownerMeta?.selected_brand_id);
        if (ownerResolved !== undefined) {
          yield put(setSelectedBrandId(ownerResolved));
        }
      }

      // Subscription plan always comes from the owner, never from the member
      if (ownerMeta?.subscription_plan) {
        yield put(setSubscriptionPlan(ownerMeta.subscription_plan as string));
      }
      
      yield put(fetchSelectedBrandIdSuccess(memberResolved ?? parseMetaSelectedBrandId(ownerMeta?.selected_brand_id)));
    } else {
      const effectiveUserId = overrideUserId ?? ownerId ?? null;
      if (effectiveUserId == null) {
        yield put(fetchSelectedBrandIdSuccess(undefined));
        return;
      }

      const meta = yield* fetchUsersMeta(effectiveUserId);
      const resolvedBrandId = parseMetaSelectedBrandId(camelcaseKeys(meta).selectedBrandId);
      if (resolvedBrandId !== undefined) {
        yield put(setSelectedBrandId(resolvedBrandId));
      }

      if (meta?.subscription_plan) {
        yield put(setSubscriptionPlan(meta.subscription_plan));
      }
      
      yield put(fetchSelectedBrandIdSuccess(resolvedBrandId));
    }
  } catch (error) {
    yield* applySagaError(error, { operationName: "brands.fetchSelectedBrandId" });
    yield put(fetchSelectedBrandIdSuccess(undefined));
  }
}

export function* brandsSaga() {
  yield takeEvery(updateBrand.type, updateBrandSaga);
  yield takeEvery(fetchBrands.type, fetchBrandsSaga);
  yield takeEvery(deleteCompetitor.type, deleteCompetitorSaga);
  yield takeEvery(changeCompetitorStatus.type, changeCompetitorStatusSaga);
  yield takeEvery(addCompetitor.type, addCompetitorSaga);
  yield takeEvery(fetchAddNewBrandCompetitors.type, fetchAddNewBrandCompetitorsSaga);
  yield takeLatest(createNewBrand.type, createNewBrandSaga);
  yield takeEvery(fetchSelectedBrandId.type, fetchSelectedBrandIdSaga);
  yield takeEvery(updateSelectedBrandId.type, updateSelectedBrandIdSaga);
}
